import React, { useEffect, useMemo, useState } from 'react';

interface MarketplaceProps {
  open: boolean;
  onClose: () => void;
  gold: number;
  cobalt: number;
  uranium: number;
  oil: number;
  lives: number;
  countries: string[];
  onUpdate: (update: { gold?: number; cobalt?: number; uranium?: number; oil?: number; lives?: number; countries?: string[] }) => void;
}

const allCountryCards = [
  'Algeria', 'Angola', 'Benin', 'Botswana', 'Burkina Faso', 'Burundi', 'Cameroon', 'Cape Verde', 'Central African Republic', 'Chad', 'Comoros', 'D.R Congo', 'Djibouti', 'Egypt', 'Equitorial Guinea', 'Eritrea', 'Eswatini', 'Ethiopia', 'Gabon', 'Gambia', 'Ghana', 'Guinea-Bissau', 'Guinea', 'Ivory Coast', 'Lesotho', 'Liberia', 'Libya', 'Madagascar', 'Malawi', 'Mali', 'Mauritania', 'Mauritius', 'Morocco', 'Mozambique', 'Namibia', 'Niger', 'Nigeria', 'Republic of Congo', 'Rwanda', 'Sao Tome and Principe', 'Senegal', 'Seychelles', 'Sierra Leone', 'Somalia', 'South Africa', 'South Sudan', 'Sudan', 'Tanzania', 'Togo', 'Tunisia', 'Uganda', 'Zambia', 'Zimbabwe'
];

const Marketplace: React.FC<MarketplaceProps> = ({ open, onClose, gold, cobalt, uranium, oil, lives, countries, onUpdate }) => {
  const [localGold, setLocalGold] = useState(gold);
  const [localCobalt, setLocalCobalt] = useState(cobalt);
  const [localUranium, setLocalUranium] = useState(uranium);
  const [localOil, setLocalOil] = useState(oil);
  const [localLives, setLocalLives] = useState(lives);
  const [localCountries, setLocalCountries] = useState<string[]>(countries);

  useEffect(() => {
    setLocalGold(gold);
    setLocalCobalt(cobalt);
    setLocalUranium(uranium);
    setLocalOil(oil);
    setLocalLives(lives);
    setLocalCountries(countries);
  }, [gold, cobalt, uranium, oil, lives, countries]);

  const remainingCountries = useMemo(
    () => allCountryCards.filter((country) => !localCountries.includes(country)),
    [localCountries]
  );

  const oilToGold = () => {
    if (localOil >= 5) {
      const nextOil = localOil - 5;
      const nextGold = localGold + 1;
      setLocalOil(nextOil);
      setLocalGold(nextGold);
      onUpdate({ oil: nextOil, gold: nextGold, countries: localCountries });
    }
  };

  const cobaltToUranium = () => {
    if (localCobalt >= 5) {
      const nextCobalt = localCobalt - 5;
      const nextUranium = localUranium + 1;
      setLocalCobalt(nextCobalt);
      setLocalUranium(nextUranium);
      onUpdate({ cobalt: nextCobalt, uranium: nextUranium, countries: localCountries });
    }
  };

  const unlockCountry = () => {
    if (localUranium >= 5 && remainingCountries.length > 0) {
      const nextCountry = remainingCountries[0];
      const nextUranium = localUranium - 5;
      const nextCountries = [...localCountries, nextCountry];
      setLocalUranium(nextUranium);
      setLocalCountries(nextCountries);
      onUpdate({ uranium: nextUranium, countries: nextCountries, gold: localGold, cobalt: localCobalt, oil: localOil, lives: localLives });
    }
  };

  const buyLife = () => {
    if (localGold >= 5) {
      const nextGold = localGold - 5;
      const nextLives = localLives + 1;
      setLocalGold(nextGold);
      setLocalLives(nextLives);
      onUpdate({ gold: nextGold, lives: nextLives, countries: localCountries });
    }
  };

  if (!open) return null;

  return (
    <div className="marketplace-modal">
      <div className="marketplace-content">
        <h2>Marketplace</h2>
        <div className="resource-row">
          <div className="resource-item">🥇 Gold: {localGold}</div>
          <div className="resource-item">🔵 Cobalt: {localCobalt}</div>
          <div className="resource-item">☢️ Uranium: {localUranium}</div>
          <div className="resource-item">🛢️ Oil: {localOil}</div>
          <div className="resource-item">❤️ Lives: {localLives}</div>
        </div>
        <button onClick={oilToGold} disabled={localOil < 5}>Convert 5 Oil → 1 Gold</button>
        <button onClick={cobaltToUranium} disabled={localCobalt < 5}>Convert 5 Cobalt → 1 Uranium</button>
        <button onClick={unlockCountry} disabled={localUranium < 5 || remainingCountries.length === 0}>
          Unlock Country Card (5 Uranium)
        </button>
        <button onClick={buyLife} disabled={localGold < 5}>Buy 1 Life (5 Gold)</button>

        <div style={{ marginTop: '1rem', textAlign: 'left' }}>
          <strong>Unlocked Country Cards ({localCountries.length}):</strong>
          {localCountries.length === 0 ? (
            <div style={{ marginTop: '0.5rem', fontStyle: 'italic' }}>No unlocked cards yet.</div>
          ) : (
            <div className="country-grid">
              {localCountries.map((country) => (
                <div key={country} className="country-card">
                  <img
                    src={`/alkebulan/Freedom/${encodeURIComponent(country)}.png`}
                    alt={country}
                    title={country}
                  />
                  <div className="country-label">{country}</div>
                </div>
              ))}
            </div>
          )}
          {remainingCountries.length > 0 && (
            <div style={{ marginTop: '0.75rem' }}>
              Next card unlock: <strong>{remainingCountries[0]}</strong>
            </div>
          )}
          {remainingCountries.length === 0 && localCountries.length > 0 && (
            <div style={{ marginTop: '0.75rem', color: '#047857' }}><strong>All cards unlocked!</strong></div>
          )}
        </div>

        <button onClick={onClose}>Close</button>
      </div>

      <style>{`
        .marketplace-modal { position: fixed; inset: 0; background: rgba(0,0,0,0.55); display: flex; align-items: center; justify-content: center; z-index: 999; padding: 1rem; }
        .marketplace-content { background: #fff; border-radius: 16px; border: 2px solid #ff8a5c; width: min(760px, 95vw); max-height: 90vh; overflow: auto; padding: 1.2rem 1rem; box-shadow: 0 10px 30px rgba(0,0,0,0.24); }
        .marketplace-content h2 { margin-top: 0; margin-bottom: 0.85rem; text-align: center; font-size: 1.3rem; }
        .marketplace-content div { margin-bottom: 0.55rem; font-weight: 600; font-size: 1rem; }
        .marketplace-content button { width: 100%; margin-top: 0.65rem; background: #f59e0b; color: #fff; border: none; border-radius: 10px; padding: 0.65rem 0.75rem; font-size: 1rem; font-weight: 600; cursor: pointer; }
        .marketplace-content button:hover:not(:disabled) { background: #d97706; }
        .marketplace-content button:disabled { opacity: 0.55; cursor: not-allowed; }
        .marketplace-content button:last-child { margin-top: 0.45rem; background: #6b7280; }
        .marketplace-content button:last-child:hover:not(:disabled) { background: #4b5563; }

        .resource-row { display: flex; flex-wrap: wrap; gap: 0.75rem; justify-content: center; margin-bottom: 0.75rem; }
        .resource-item { background: #f9fafb; border: 1px solid #d1d5db; border-radius: 999px; padding: 0.38rem 0.8rem; font-size: 0.95rem; font-weight: 700; }

        .country-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); gap: 0.5rem; margin-top: 0.5rem; }
        .country-card { display: flex; flex-direction: column; align-items: center; }
        .country-card img { width: 72px; height: 48px; object-fit: contain; border: 1px solid #d1d5db; border-radius: 6px; background: #fafafa; }
        .country-label { margin-top: 0.25rem; font-size: 0.7rem; font-weight: 600; text-align: center; }

        @media (max-width: 640px) {
          .marketplace-content { width: 96vw; padding: 0.8rem; }
          .marketplace-content h2 { font-size: 1.1rem; }
          .marketplace-content div { font-size: 0.95rem; }
          .marketplace-content button { font-size: 0.95rem; }
          .country-grid { grid-template-columns: repeat(auto-fill, minmax(72px, 1fr)); }
        }
      `}</style>
    </div>
  );
};

export default Marketplace;
